import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useWishlist } from '../Context/WishlistContext'
import { useCart } from '../Context/CartContext'
import { useAuth } from '../Context/AuthContext'
import { useCurrency } from '../Context/CurrencyContext'
import useTemplateScripts from '../hooks/useTemplateScripts'
import MainHeader from './MainHeader'
import HomeFooter from './HomeFooter'
import CartDrawer from './CartDrawer'
import { SHOP_CATEGORIES, classifyCategory, collectionPath } from '../constants/shopCategories'
import { CONTACT_INFO, SITE_LINKS } from '../constants/siteLinks'
import './Wishlist.css'

const WISHLIST_PERKS = [
  { icon: 'fa-certificate', title: 'Genuine products', text: 'With warranty cover' },
  { icon: 'fa-truck-fast', title: 'Delivery across Rwanda', text: 'Kigali next-day dispatch' },
  { icon: 'fa-screwdriver-wrench', title: 'Professional installation', text: 'By Fabritech engineers' },
]

const productPath = (item) => `/product-details/${item.productId || item.id}`

/** Sub-category name for a saved item (e.g. "CCTV Cameras"), when it has a category. */
const typeOf = (item) => (item.category ? classifyCategory(item.category, item.name).subcategoryName : '')

function Wishlist() {
  useTemplateScripts()
  const navigate = useNavigate()
  const { wishlistItems, removeFromWishlist, clearWishlist, loading } = useWishlist()
  const { cartItems, addToCart } = useCart()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const { formatPrice } = useCurrency()
  const [showCart, setShowCart] = useState(false)
  const [toast, setToast] = useState(null)
  const [addedId, setAddedId] = useState(null)

  // Signed-in customers only (wait for the saved session to load first)
  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate(SITE_LINKS.login)
  }, [authLoading, isAuthenticated, navigate])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 2400)
    return () => clearTimeout(timer)
  }, [toast])

  if (!isAuthenticated) return null

  const count = wishlistItems.length
  const total = wishlistItems.reduce((sum, item) => sum + (Number(item.price) || 0), 0)

  const addOne = (item) => {
    addToCart(item, 1)
    setAddedId(item.id)
    setTimeout(() => setAddedId(null), 1800)
    setToast(`${item.name} added to cart`)
  }

  const addAll = () => {
    wishlistItems.forEach((item) => addToCart(item, 1))
    setToast(`${count} ${count === 1 ? 'item' : 'items'} added to cart`)
  }

  const remove = async (item) => {
    try {
      await removeFromWishlist(item.id)
      setToast(`${item.name} removed from wishlist`)
    } catch {
      setToast('Couldn’t update your wishlist — please try again')
    }
  }

  const clearAll = async () => {
    if (!window.confirm('Remove all items from your wishlist?')) return
    try {
      await clearWishlist()
    } catch {
      setToast('Couldn’t update your wishlist — please try again')
    }
  }

  return (
    <div className="wl-page">
      <MainHeader cartCount={cartItems.length} onCartClick={() => setShowCart((open) => !open)} />

      <main className="wl-main">
        <div className="wl-container">
          <section className="wl-hero">
            <div className="wl-hero__copy">
              <nav className="wl-crumbs" aria-label="Breadcrumb">
                <Link to={SITE_LINKS.home}>Home</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <Link to={SITE_LINKS.shop}>Shop</Link>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                <span aria-current="page">Wishlist</span>
              </nav>
              <div className="wl-hero__heading">
                <span className="wl-hero__icon" aria-hidden="true">
                  <i className="fa-solid fa-heart" />
                </span>
                <div>
                  <h1 className="wl-hero__title">My Wishlist</h1>
                  <p className="wl-hero__text">Products you’ve saved for later — add them to your cart whenever you’re ready.</p>
                </div>
              </div>
            </div>

            {count > 0 && (
              <aside className="wl-summary" aria-label="Wishlist summary">
                <dl className="wl-summary__stats">
                  <div>
                    <dt>Saved items</dt>
                    <dd>{count}</dd>
                  </div>
                  <div>
                    <dt>Total value</dt>
                    <dd>{formatPrice(total)}</dd>
                  </div>
                </dl>
                <button type="button" className="wl-btn wl-btn--primary wl-btn--block" onClick={addAll}>
                  <i className="fa-solid fa-cart-plus" aria-hidden="true" />
                  Add all to cart
                </button>
                <button type="button" className="wl-summary__clear" onClick={clearAll}>
                  <i className="fa-solid fa-trash-can" aria-hidden="true" />
                  Clear wishlist
                </button>
              </aside>
            )}
          </section>

          {loading ? (
            <ul className="wl-grid" aria-busy="true" aria-label="Loading wishlist">
              {Array.from({ length: 4 }, (_, i) => (
                <li key={i} className="wl-card wl-card--skeleton">
                  <span className="wl-card__media" />
                  <span className="wl-skel wl-skel--short" />
                  <span className="wl-skel" />
                  <span className="wl-skel wl-skel--btn" />
                </li>
              ))}
            </ul>
          ) : count === 0 ? (
            <section className="wl-empty">
              <span className="wl-empty__art" aria-hidden="true">
                <i className="fa-regular fa-heart" />
              </span>
              <h2 className="wl-empty__title">Your wishlist is empty</h2>
              <p className="wl-empty__text">
                Tap the <i className="fa-regular fa-heart" aria-hidden="true" /> on any product to save it here and come back to it later.
              </p>
              <Link to={SITE_LINKS.shop} className="wl-btn wl-btn--primary">
                Browse the shop
                <i className="fa-solid fa-arrow-right" aria-hidden="true" />
              </Link>
              <div className="wl-empty__cats">
                <span>Popular categories</span>
                <ul>
                  {SHOP_CATEGORIES.map((cat) => (
                    <li key={cat.id}>
                      <Link to={collectionPath(cat.id)} className="wl-catlink">
                        <i className={`fa-solid ${cat.icon}`} aria-hidden="true" />
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          ) : (
            <>
              <div className="wl-bar">
                <p>
                  <strong>
                    {count} {count === 1 ? 'product' : 'products'}
                  </strong>{' '}
                  saved
                </p>
                <Link to={SITE_LINKS.shop} className="wl-bar__link">
                  Continue shopping
                  <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                </Link>
              </div>

              <ul className="wl-grid">
                {wishlistItems.map((item) => {
                  const type = typeOf(item)
                  const added = addedId === item.id
                  return (
                    <li key={item.id} className="wl-card">
                      <Link to={productPath(item)} state={{ product: item }} className="wl-card__media">
                        <img
                          src={item.image || '/placeholder-image.png'}
                          alt={item.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder-image.png'
                          }}
                        />
                      </Link>
                      <button
                        type="button"
                        className="wl-card__remove"
                        onClick={() => remove(item)}
                        aria-label={`Remove ${item.name} from wishlist`}
                        title="Remove"
                      >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                      </button>
                      <span className="wl-card__saved" aria-hidden="true">
                        <i className="fa-solid fa-heart" />
                      </span>

                      <div className="wl-card__body">
                        {type && <span className="wl-card__type">{type}</span>}
                        <h3 className="wl-card__name">
                          <Link to={productPath(item)} state={{ product: item }}>
                            {item.name}
                          </Link>
                        </h3>
                        <p className="wl-card__price">{formatPrice(item.price || 0)}</p>
                        <div className="wl-card__actions">
                          <button
                            type="button"
                            className={`wl-btn wl-btn--primary wl-btn--block${added ? ' is-added' : ''}`}
                            onClick={() => addOne(item)}
                          >
                            <i className={`fa-solid ${added ? 'fa-check' : 'fa-cart-shopping'}`} aria-hidden="true" />
                            {added ? 'Added' : 'Add to Cart'}
                          </button>
                          <Link
                            to={productPath(item)}
                            state={{ product: item }}
                            className="wl-iconbtn"
                            aria-label={`View ${item.name}`}
                            title="View product"
                          >
                            <i className="fa-solid fa-eye" aria-hidden="true" />
                          </Link>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </>
          )}

          <section className="wl-help">
            <ul className="wl-help__perks">
              {WISHLIST_PERKS.map((perk) => (
                <li key={perk.title}>
                  <span className="wl-help__icon" aria-hidden="true">
                    <i className={`fa-solid ${perk.icon}`} />
                  </span>
                  <span>
                    <strong>{perk.title}</strong>
                    {perk.text}
                  </span>
                </li>
              ))}
            </ul>
            <div className="wl-help__cta">
              <span>
                <strong>Need advice before you buy?</strong>
                Our engineers will help you choose.
              </span>
              <a href={CONTACT_INFO.phoneHref} className="wl-btn wl-btn--outline">
                <i className="fa-solid fa-phone" aria-hidden="true" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </section>
        </div>
      </main>

      <HomeFooter />

      <CartDrawer open={showCart} onClose={() => setShowCart(false)} onToast={(message) => setToast(message)} />

      {toast && (
        <div className="wl-toast" role="status">
          <i className="fa-solid fa-circle-check" aria-hidden="true" />
          {toast}
        </div>
      )}
    </div>
  )
}

export default Wishlist
